import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SelahDailyPrivacyPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="selah-daily-privacy-page">
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <Link to="/apps" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Apps
            </Link>
            
            <div className="flex items-center gap-4 mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_app-collective/artifacts/8hbgdbmy_Screenshot%202026-03-05%20at%207.43.13%E2%80%AFPM.png" 
                alt="Selah Daily" 
                className="w-16 h-16 rounded-2xl"
              />
              <div>
                <p className="caption mb-1">Privacy Policy</p>
                <h1 className="heading-2">Selah Daily</h1>
              </div>
            </div>
            
            <div className="prose prose-zinc max-w-none">
              <p className="body-text mb-2"><strong>Effective Date:</strong> February 15, 2026</p>
              <p className="body-text mb-8"><strong>Last Updated:</strong> February 15, 2026</p>

              <div className="space-y-8 body-text">
                <p>
                  Selah Daily ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and protect information when you use the Selah Daily mobile application (the "App").
                </p>
                <p>By using Selah Daily, you agree to this Privacy Policy.</p>

                <section>
                  <h2 className="heading-3 mb-4">1. Information We Collect</h2>
                  <p>We may collect the following categories of information:</p>
                  
                  <h4 className="font-semibold mt-4 mb-2">Account Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Authentication data from sign-in providers (Apple and Google)</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">User Content</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Sermon audio recordings</li>
                    <li>Sermon titles and speaker names</li>
                    <li>Transcriptions, summaries, key points, devotionals, translations</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">Subscription and Purchase Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Subscription status and plan tier (Free, Growth, Ultimate Growth, Yearly)</li>
                    <li>Purchase/receipt metadata from Apple In-App Purchases</li>
                    <li>We do not collect full payment card details; payments are handled by Apple</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">Device and Usage Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>App usage events (e.g., features used)</li>
                    <li>Device information (e.g., OS version, app version)</li>
                    <li>Crash and performance diagnostics</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">Notification Preferences</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Reminder settings (push/email toggle, reminder time)</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">2. How We Use Information</h2>
                  <p>We use your information to:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-1">
                    <li>Create and manage your account</li>
                    <li>Record, store, and process sermon audio</li>
                    <li>Generate transcriptions, summaries, key points, devotionals, and translations</li>
                    <li>Provide bookmarking, notes, and devotional tracking/streaks</li>
                    <li>Send reminders and notifications based on your preferences</li>
                    <li>Process and validate subscriptions and restore purchases</li>
                    <li>Improve app performance, reliability, and user experience</li>
                    <li>Detect, prevent, and address fraud, abuse, and security issues</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">3. AI Processing</h2>
                  <p>
                    Selah Daily uses AI services to process sermon content (including audio and text) for transcription, summarization, devotional generation, and translation.
                  </p>
                  <p className="mt-2">
                    Audio/text content may be securely sent to our backend and AI providers to fulfill these features.
                  </p>
                  <p className="mt-2 font-medium">We do not sell your sermon content.</p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">4. How We Share Information</h2>
                  <p>We may share information with:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-1">
                    <li>Service providers that help us operate the App (e.g., cloud hosting, authentication, analytics, AI processing)</li>
                    <li>Apple for subscription and purchase verification</li>
                    <li>Legal authorities when required by law or to protect rights and safety</li>
                    <li>Business transfers if we are involved in a merger, acquisition, or asset sale</li>
                  </ul>
                  <p className="mt-4 font-medium">We do not sell personal information.</p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">5. Data Retention</h2>
                  <p>
                    We retain your information for as long as needed to provide the App and for legitimate business/legal purposes.
                  </p>
                  <p className="mt-2">
                    If you stop using Selah Daily, some data may remain in backups or logs for a limited period as required for security, legal, or operational reasons.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">6. Account Deletion</h2>
                  <p>
                    You may request account deletion by contacting us at{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                  <p className="mt-2">When your account is deleted:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>We delete or anonymize personal data we control, except where retention is required by law, security, fraud prevention, billing, or dispute resolution obligations.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">7. Security</h2>
                  <p>
                    We use reasonable administrative, technical, and organizational safeguards to protect your information.
                  </p>
                  <p className="mt-2">
                    However, no system is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">8. Children's Privacy</h2>
                  <p>
                    We do not knowingly collect personal information from children. If you believe a child has provided personal data without parental knowledge, contact us at{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">9. Your Rights and Choices</h2>
                  <p>
                    Depending on your location, you may have rights to access, correct, delete, or restrict use of your personal information.
                  </p>
                  <p className="mt-2">
                    To exercise rights, contact us at{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                  <p className="mt-4">You may also:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Disable push notifications in device settings</li>
                    <li>Manage subscriptions through your Apple ID subscription settings</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">10. International Data Transfers</h2>
                  <p>
                    Your information may be processed in countries other than your own. We take reasonable steps to ensure appropriate safeguards are in place where required.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">11. Third-Party Services</h2>
                  <p>Selah Daily uses third-party services, which may have their own privacy policies, including:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-1">
                    <li>Apple Sign In / Apple In-App Purchases</li>
                    <li>Google Sign-In / Firebase</li>
                    <li>AI processing providers</li>
                  </ul>
                  <p className="mt-4">We encourage you to review those providers' privacy terms.</p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">12. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. We will update the "Last Updated" date and, where required, provide additional notice.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">13. Contact Us</h2>
                  <p>If you have questions or requests about this Privacy Policy, contact:</p>
                  <p className="mt-4">
                    <strong>The App Fam</strong><br />
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SelahDailyPrivacyPage;
